import React, { useCallback, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import type { LogEntry } from "../../API/Models";
import { Modal } from "../Modal";
import { useUserDirectory } from "../Account/useUserDirectory";
import { ChangesFeedList } from "./ChangesFeed";
import { activityFeedClient } from "./activityFeedClient";

export const PAGE_SIZE = 40;

export interface RecentChangesModalProps {
	open: boolean;
	/** "me" → the caller's own journal; "all" → the whole team's journal. */
	scope: "me" | "all";
	currentUser: string;
	/** Email du compte connecté : clé stable pour « Vous » (les noms d'affichage se dupliquent). */
	currentUserEmail?: string;
	onClose: () => void;
}

/**
 * Full, server-paginated view of a change feed, opened from a panel's "Voir tout" button. Fetches one
 * page at a time (true server-side pagination : no client cap): the "all" scope reads the whole team's
 * journal, the "me" scope reads the caller's own. Rows reuse ChangesFeedList so they look identical to
 * the dashboard panel. EVERYTHING is shown here : the ForexAPI beat included : and NOTHING is folded:
 * one row = one entry.
 */
export const RecentChangesModal: React.FC<RecentChangesModalProps> = ({ open, scope, currentUser, currentUserEmail, onClose }) => {
	const { resolveId, resolvePicture } = useUserDirectory();
	const [page, setPage] = useState(1);
	const [items, setItems] = useState<LogEntry[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);

	// Reset to the first page whenever the modal (re)opens or the scope changes.
	useEffect(() => {
		if (open) setPage(1);
	}, [open, scope]);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = scope === "me" ? await activityFeedClient.mine(page, PAGE_SIZE) : await activityFeedClient.all(page, PAGE_SIZE);
			setItems(res.items ?? []);
			setTotal(res.total ?? 0);
		} catch {
			setItems([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [scope, page]);

	useEffect(() => {
		if (open) load();
	}, [open, load]);

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const title = scope === "me" ? "Mes dernières modifications" : "Toutes les modifications récentes";
	const emptyText = scope === "me" ? "Aucune modification de votre part." : "Aucune activité récente.";

	return (
		<Modal show={open} onHide={onClose} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="p-0" scrollable>
				{loading ? (
					<ul className="divide-y divide-slate-100">
						{Array.from({ length: 8 }).map((_, i) => (
							<li key={`rcm-skel-${i}`} className="flex items-center gap-3 px-5 py-3">
								<span className="h-7 w-7 rounded-full bg-slate-100" />
								<div className="h-3 w-2/3 rounded bg-slate-100" />
							</li>
						))}
					</ul>
				) : items.length === 0 ? (
					<div className="px-5 py-16 text-center text-sm text-slate-500">{emptyText}</div>
				) : (
					<ChangesFeedList entries={items} currentUser={currentUser} currentUserEmail={currentUserEmail} resolveUserId={resolveId} resolveUserPicture={resolvePicture} />
				)}
			</Modal.Body>
			<Modal.Footer className="justify-between">
				<span className="text-xs text-slate-500">
					{total > 0 ? `${total} entrée${total > 1 ? "s" : ""} · page ${page} / ${totalPages}` : "Aucune entrée"}
				</span>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={loading || page <= 1}
						className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
					>
						<FiChevronLeft className="h-3.5 w-3.5" /> Précédent
					</button>
					<button
						type="button"
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={loading || page >= totalPages}
						className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
					>
						Suivant <FiChevronRight className="h-3.5 w-3.5" />
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default RecentChangesModal;
